/**
 * Iter 45 — Provider-facing fidelity summary card for Provider Reports
 * Service Delivery tab.
 *
 * Renders headline tiles (Behind / On track / Met / Over / No data)
 * plus a list of students behind OR over, so the provider can see who
 * needs catch-up and who to re-scope.
 */
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Gauge, ArrowRight } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= when drilling into student fidelity.
import useShadowNavigate from "@/lib/useShadowNavigate";
import { FidelityChip, FIDELITY_META } from "./FidelityVisuals";
import api from "@/lib/api";

function Tile({ label, value, tone, testid }) {
  const toneClass = tone === "red"
    ? "bg-[#FBEFEC]/60 border-[#B85C5C]/25 text-[#B85C5C]"
    : tone === "amber"
      ? "bg-[#FDF8F3] border-[#9C6B2E]/20 text-[#9C6B2E]"
      : tone === "green"
        ? "bg-[#E8F0EB]/60 border-[#1E5A3F]/20 text-[#1E5A3F]"
        : tone === "blue"
          ? "bg-[#EEF2F7] border-[#4A6B8C]/20 text-[#4A6B8C]"
          : "bg-white border-[#E5E3DB] text-[#6B7280]";
  return (
    <div
      className={`rounded-xl border p-4 ${toneClass}`}
      data-testid={testid}
    >
      <p className="text-[10px] uppercase tracking-wider font-semibold">{label}</p>
      <p className="text-3xl font-bold tabular-nums mt-2 text-[#1F2937]">{value}</p>
    </div>
  );
}

export function ProviderFidelityReport({ providerId, testid = "provider-fidelity-report" }) {
  const navigate = useShadowNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!providerId) return;
    setLoading(true);
    api
      .getProviderFidelity({ provider_id: providerId })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [providerId]);

  if (!providerId) return null;
  if (loading) return <Skeleton className="h-48" />;
  if (!data) return null;

  const agg = data.aggregate || {};
  const students = data.students || [];
  const behindList = students.filter((s) => s.status_rollup === "behind");
  const overList = students.filter((s) => s.status_rollup === "over");
  // FP3-034D — Make-up needed students surface in their own column
  // so providers can plan make-ups distinctly from "true behind".
  const makeUpList = students.filter((s) => s.status_rollup === "needs_makeup");

  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="flex items-center gap-2">
        <Gauge size={18} weight="duotone" className="text-[#1E3F33]" />
        <h3 className="text-base font-semibold text-[#1F2937] heading-font">
          Minutes fidelity
        </h3>
        <span className="text-[11px] text-[#9B9890]">as of {data.as_of}</span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile label="Behind" value={agg.behind || 0} tone="red" testid="fidelity-tile-behind" />
        <Tile label="Needs make-up" value={agg.needs_makeup || 0} tone="amber" testid="fidelity-tile-needs-makeup" />
        <Tile label="On track" value={agg.on_track || 0} tone="blue" testid="fidelity-tile-on-track" />
        <Tile label="Met" value={(agg.met || 0) + (agg.met_after_exclusions || 0)} tone="green" testid="fidelity-tile-met" />
        <Tile label="Over" value={agg.over || 0} tone="amber" testid="fidelity-tile-over" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FidelityStudentList
          title="Behind — need catch-up this period"
          students={behindList}
          emptyMsg="No students behind. Nice work keeping the caseload current."
          tone="red"
          testid="fidelity-behind-list"
          onSelect={(id) => navigate(`/students/${id}?tab=overview`)}
        />
        <FidelityStudentList
          title="Needs make-up — meeting / conference / non-excused"
          students={makeUpList}
          emptyMsg="Nothing needs make-up right now."
          tone="amber"
          testid="fidelity-makeup-list"
          onSelect={(id) => navigate(`/students/${id}?tab=overview`)}
        />
        <FidelityStudentList
          title="Over — consider re-scoping"
          students={overList}
          emptyMsg="No students over committed minutes."
          tone="amber"
          testid="fidelity-over-list"
          onSelect={(id) => navigate(`/students/${id}?tab=overview`)}
        />
      </div>
    </div>
  );
}

function FidelityStudentList({ title, students, emptyMsg, tone, testid, onSelect }) {
  return (
    <Card className="border-[#E5E3DB]" data-testid={testid}>
      <CardHeader className="pb-2">
        <CardTitle className="text-[13px] font-semibold text-[#1F2937] heading-font">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {students.length === 0 && (
          <p className="text-[11px] text-[#9B9890] italic py-2">{emptyMsg}</p>
        )}
        <ul className="divide-y divide-[#ECEAE4]">
          {students.map((s) => {
            const primary = s.commitments?.[0];
            return (
              <li key={s.student_id}>
                <button
                  type="button"
                  onClick={() => onSelect?.(s.student_id)}
                  className="w-full py-2 flex items-center gap-3 hover:bg-[#F8F7F3]/60 rounded-md px-1.5 transition-colors text-left"
                  data-testid={`${testid}-item-${s.student_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] font-medium text-[#1F2937] truncate">
                      {s.first_name} {s.last_name}
                    </p>
                    {primary && (
                      <p className="text-[10.5px] text-[#6B7280] tabular-nums">
                        {primary.delivered_minutes}/{primary.target_minutes} min this {primary.cadence}
                        <span className="text-[#9B9890] ml-1">· {primary.label}</span>
                      </p>
                    )}
                  </div>
                  <FidelityChip status={s.status_rollup} size="xs" />
                  <ArrowRight size={12} className="text-[#9B9890] shrink-0" />
                </button>
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

// Re-export so the report surface can import from one place.
export { FIDELITY_META };
